import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { DiscoveryRequest } from "../models/DiscoveryRequest";
import { DiscoveryRequestService } from "../services/DiscoveryRequestService";
import { useAuth } from "./AuthContext";
import { Task } from "../models/Task";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { Subtask } from "../models/Subtask";
import { DiscoveryRequestTask } from "../models/DiscoveryRequestTask";

interface Errors {
  list?: Error | null;
  details?: Error | null;
  tasks?: Error | null; // For discovery request tasks loading
}

interface Loadings {
  list?: boolean;
  details?: boolean;
  tasks?: boolean;
  recommendedTasks?: boolean;
}

interface DiscoveryRequestContextProps {
  discoveryRequests: DiscoveryRequest[];
  tasks: DiscoveryRequestTask[];
  selectedDiscoveryRequest: DiscoveryRequest | null;
  setSelectedDiscoveryRequest: (discoveryRequestId: string) => void;
  createDiscoveryRequest: (
    file: globalThis.File | null,
    discoveryRequestData: Omit<
      DiscoveryRequest,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId"
    >,
  ) => Promise<string>;
  updateDiscoveryRequest: (
    discoveryRequestId: string,
    updatedData: Partial<
      Omit<DiscoveryRequest, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteDiscoveryRequestById: (discoveryRequestId: string) => void;
  getRecommendedTasks: (discoveryRequestId: string) => Promise<string[] | null>;
  insertTasks: (discoveryRequestId: string, tasks: string[]) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const DiscoveryRequestContext = createContext<
  DiscoveryRequestContextProps | undefined
>(undefined);

export const useDiscoveryRequest = (): DiscoveryRequestContextProps => {
  const context = useContext(DiscoveryRequestContext);
  if (!context) {
    throw new Error(
      "useDiscoveryRequest must be used within a DiscoveryRequestProvider",
    );
  }
  return context;
};

export const DiscoveryRequestProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [discoveryRequests, setDiscoveryRequests] = useState<
    DiscoveryRequest[]
  >([]);
  const [tasks, setTasks] = useState<DiscoveryRequestTask[]>([]);
  const [selectedDiscoveryRequest, setSelectedDiscoveryRequest] =
    useState<DiscoveryRequest | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  const fetchDiscoveryRequests = async () => {
    if (!user || !currentMatterId) return;
    try {
      const fetchedDiscoveryRequests =
        await MatterService.getDiscoveryRequests(currentMatterId);
      setDiscoveryRequests(fetchedDiscoveryRequests);
      setLoadings((prev) => ({ ...prev, list: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  const fetchTasks = async () => {
    if (!user || !currentMatterId || !selectedDiscoveryRequest) return;
    try {
      const relatedTasks = await DiscoveryRequestService.getTasks(
        selectedDiscoveryRequest._id,
      );
      setTasks(relatedTasks);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, tasks: err }));
      setLoadings((prev) => ({ ...prev, tasks: false }));
    }
  };

  // Fetch all discoveryRequests
  useEffect(() => {
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchDiscoveryRequests();
  }, [user, currentMatterId]);

  useSocketEventByIds(
    "discoveryrequests:changed",
    ["*"],
    (updatedDocument: DiscoveryRequest) => {
      console.log("DiscoveryRequests changed event received:", updatedDocument);
      if (
        selectedDiscoveryRequest &&
        updatedDocument._id === selectedDiscoveryRequest._id
      ) {
        setSelectedDiscoveryRequest(updatedDocument);
      } else if (
        discoveryRequests.some((dr) => dr._id === updatedDocument._id)
      ) {
        const index = discoveryRequests.findIndex(
          (dr) => dr._id === updatedDocument._id,
        );
        discoveryRequests[index] = updatedDocument;
      } else {
        discoveryRequests.push(updatedDocument);
      }
    },
  );

  useSocketEventByIds(
    "discoveryrequesttasks:changed",
    ["*"],
    (updatedDocument) => {
      if (
        selectedDiscoveryRequest &&
        updatedDocument?.discoveryRequest === selectedDiscoveryRequest._id
      ) {
        fetchTasks(); // Refresh tasks if the selected discovery request was updated
      }
    },
  );

  const setSelectedDiscoveryRequestById = async (
    discoveryRequestId: string,
  ) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const discoveryRequestData =
        await DiscoveryRequestService.getDiscoveryRequest(discoveryRequestId);
      const relatedTasks =
        await DiscoveryRequestService.getTasks(discoveryRequestId);
      setSelectedDiscoveryRequest(discoveryRequestData);
      setTasks(relatedTasks);
      setLoadings((prev) => ({ ...prev, details: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createDiscoveryRequest = async (
    file: globalThis.File | null,
    discoveryRequestData: Omit<
      DiscoveryRequest,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ): Promise<string> => {
    const newDiscoveryRequestId =
      await DiscoveryRequestService.createDiscoveryRequest(file, {
        ...discoveryRequestData,
        matterId: currentMatterId!,
      });
    return newDiscoveryRequestId;
  };

  const updateDiscoveryRequest = async (
    discoveryRequestId: string,
    updatedData: Partial<
      Omit<DiscoveryRequest, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    if (!currentMatterId) return;
    await DiscoveryRequestService.updateDiscoveryRequest(
      currentMatterId,
      discoveryRequestId,
      updatedData,
    );
    await setSelectedDiscoveryRequestById(discoveryRequestId);
  };

  const deleteDiscoveryRequestById = async (discoveryRequestId: string) => {
    if (!currentMatterId) return;
    await DiscoveryRequestService.deleteDiscoveryRequest(discoveryRequestId);
    setDiscoveryRequests((prev) =>
      prev.filter((t) => t._id !== discoveryRequestId),
    );
    setSelectedDiscoveryRequest(null);
  };

  const getRecommendedTasks = async (
    discoveryRequestId: string,
  ): Promise<string[] | null> => {
    if (!currentMatterId) return null;
    try {
      setLoadings((prev) => ({ ...prev, recommendedTasks: true }));
      const recommendedTasks =
        await DiscoveryRequestService.getRecommendedTasks(
          currentMatterId,
          discoveryRequestId,
        );
      console.log("Recommended tasks:", recommendedTasks);
      return recommendedTasks;
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, tasks: err }));
      return null;
    } finally {
      setLoadings((prev) => ({ ...prev, recommendedTasks: false }));
    }
  };

  const insertTasks = async (discoveryRequestId: string, tasks: string[]) => {
    await DiscoveryRequestService.insertTasks(
      currentMatterId!,
      discoveryRequestId,
      tasks,
    );
  };

  return (
    <DiscoveryRequestContext.Provider
      value={{
        tasks,
        loading: loadings,
        error: errors,
        discoveryRequests,
        selectedDiscoveryRequest,
        createDiscoveryRequest,
        updateDiscoveryRequest,
        deleteDiscoveryRequestById,
        setSelectedDiscoveryRequest: setSelectedDiscoveryRequestById,
        getRecommendedTasks,
        insertTasks,
      }}
    >
      {children}
    </DiscoveryRequestContext.Provider>
  );
};
